/**
 * KeyboardLayer — the pharmacy shell's keyboard layer (design/ux/01_foundations.md
 * §4, §6.5; WP-17). Mounted once by AppShell.
 *
 *  · Installs the ONE capture-phase shortcut dispatcher and the window-level
 *    scanner listener (hooks/useShortcuts.js, hooks/useScanInput.js).
 *  · Binds the global keys it owns: F1 (the sheet), Ctrl+K (the palette) and
 *    Échap with no overlay open (clears the focused search field). Alt+M and
 *    Alt+1…9 are bound by the navigation (components/layout/nav: the rail,
 *    or the drawer below 1024 px), Ctrl+Maj+A by the alertes-équipe console.
 *  · Records the pages visited for the palette's « Récents » (per user).
 */
import * as React from "react";
import { useLocation } from "react-router-dom";
import { flattenNav } from "@/lib/nav-tree.js";
import { PALETTE_VERBS } from "@/config/navigation.config.js";
import { userModules } from "@/config/modules.config.js";
import { PALETTE_RECENTS_MAX, PALETTE_RECENTS_STORAGE_PREFIX } from "@/config/ui.config.js";
import { can } from "@/config/permissions.config.js";
import {
  buildLeafIndex,
  buildVerbIndex,
  pageForPath,
  readRecents,
  recentsKey,
  recordRecent,
  visibleRecents,
} from "@/lib/command-palette.js";
import {
  installShortcutDispatcher,
  isModalOverlayOpen,
  shortcutRegistry,
  useShortcuts,
  useShortcutRegistryVersion,
} from "@/hooks/useShortcuts.js";
import { installScanListener } from "@/hooks/useScanInput.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useKeyboardStore } from "@/stores/keyboardStore.js";
import { ShortcutSheet } from "./ShortcutSheet";
import { CommandPalette } from "./CommandPalette";

/**
 * Échap with no overlay: clear a focused search field that holds text (§4.1). Exported for the
 * fournisseur portal's keyboard layer (WP-36), which binds the same global key.
 */
export function clearFocusedSearch(e: KeyboardEvent): boolean {
  const t = e.target as HTMLInputElement | null;
  if (!t || t.tagName !== "INPUT") return false;
  const isSearch = t.type === "search" || t.hasAttribute("data-shortcut-search");
  if (!isSearch || t.getAttribute("role") === "combobox" || !t.value) return false;
  // Through the native setter so React's onChange sees the change.
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  setter?.call(t, "");
  t.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}

export const KeyboardLayer: React.FC = () => {
  const user = useAuthStore((s: any) => s.user);
  // No role, no pages: the palette never falls back to a wider menu than the
  // session's own (the pre-WP-17 palette defaulted to PROPRIETAIRE).
  const role: string = user?.role ?? "";
  const helpOpen = useKeyboardStore((s) => s.helpOpen);
  const paletteOpen = useKeyboardStore((s) => s.paletteOpen);
  const toggleHelp = useKeyboardStore((s) => s.toggleHelp);
  const closeHelp = useKeyboardStore((s) => s.closeHelp);
  const openPalette = useKeyboardStore((s) => s.openPalette);
  const closePalette = useKeyboardStore((s) => s.closePalette);
  const location = useLocation();

  React.useEffect(() => installShortcutDispatcher(), []);
  React.useEffect(() => installScanListener(), []);

  // Whether a modal was already open when F1 was pressed (then the sheet
  // documents that dialog's keys, not the page's).
  const modalAtOpen = React.useRef(false);
  useShortcuts("GLOBAL", {
    AIDE: () => {
      if (!useKeyboardStore.getState().helpOpen) modalAtOpen.current = isModalOverlayOpen();
      toggleHelp();
    },
    PALETTE: () => openPalette(),
    FERMER: (e: KeyboardEvent) => clearFocusedSearch(e),
  });

  // Every page the session's menu shows (NAV_TREE / flat menus, WP-18).
  const modulesKey = (userModules(user) ?? ["?"]).join("|");
  const pages = React.useMemo(
    () => (role ? buildLeafIndex(flattenNav(user)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, modulesKey],
  );
  const storageKey = recentsKey(PALETTE_RECENTS_STORAGE_PREFIX, user?.id);
  const [recents, setRecents] = React.useState(() => readRecents(storageKey));
  React.useEffect(() => setRecents(readRecents(storageKey)), [storageKey]);
  React.useEffect(() => {
    const page = pageForPath(pages, location.pathname);
    if (page) setRecents(recordRecent(storageKey, page, PALETTE_RECENTS_MAX));
  }, [location.pathname, pages, storageKey]);
  const shownRecents = React.useMemo(() => visibleRecents(recents, pages), [recents, pages]);
  // The command verbs the role reaches, targeted at the current page when it hosts them (RC-132).
  const verbs = React.useMemo(
    () => buildVerbIndex(PALETTE_VERBS, pages, { pathname: location.pathname, search: location.search }),
    [pages, location.pathname, location.search],
  );

  // Navigating closes both overlays (the route-change focus rule applies).
  React.useEffect(() => {
    closeHelp();
    closePalette();
  }, [location.pathname, closeHelp, closePalette]);

  const version = useShortcutRegistryVersion();
  const [sheetData, setSheetData] = React.useState(() => shortcutRegistry.snapshot());
  const [pageTitle, setPageTitle] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!helpOpen) return;
    setSheetData(shortcutRegistry.snapshot({ modal: modalAtOpen.current }));
    // The page's menu label (== its h1, AUD-NAV-18; « Tableau de bord », not
    // the dashboard greeting), else the h1 of a page that is not a menu leaf.
    setPageTitle(
      pageForPath(pages, location.pathname)?.label ||
        document.querySelector("#main h1")?.textContent?.trim() ||
        null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpOpen, version]);

  const managesCatalogue = role !== "ADMIN_PHARMAKON" && can(user, "produit", "update");
  const canSearchProducts = Boolean(user?.pharmacie_id) && can(user, "produit", "read");
  // XWP-12 / WP-37 — the palette's « Lots » group reads the lot list (stock:read).
  const canSearchLots = Boolean(user?.pharmacie_id) && can(user, "stock", "read");

  return (
    <>
      <ShortcutSheet open={helpOpen} onClose={closeHelp} data={sheetData} pageTitle={pageTitle} />
      {paletteOpen && (
        <CommandPalette
          open
          onClose={closePalette}
          pages={pages}
          verbs={verbs}
          recents={shownRecents}
          managesCatalogue={managesCatalogue}
          canSearchProducts={canSearchProducts}
          canSearchLots={canSearchLots}
        />
      )}
    </>
  );
};

export default KeyboardLayer;
